import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDemoMode } from './DemoMode';
import { DEMO_FEATURES, BUTTON_COPY } from '../../data/dikshaGuidedDemo';

/**
 * DikshaGuidedDemo — a deep, page-by-page guided demo led by "Diksha".
 *  1) Diksha welcome card.
 *  2) Index of 18 features — pick any to open that real page.
 *  3) On the page: a Diksha intro + a driver.js walkthrough of EVERY visible
 *     button/tab/control (curated copy where available, else the control's label).
 *  4) After exploring → "Demo complete" funnel → Onboarding Schedule → Activate.
 *
 * Starts only via the window event 'diksha-demo:start' (Header button) — it does NOT
 * auto-run, so it never clashes with the existing quick SchoolNavTour.
 */
export default function DikshaGuidedDemo({ user, token }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { state: demoState, openActivateModal } = useDemoMode();

  const API = process.env.REACT_APP_BACKEND_URL;
  const isSchoolUser = !!user && (user.role === 'school_admin' || !!demoState?.is_demo);

  const t = (hi, en) => (language === 'hi' ? hi : en);
  const [phase, setPhase] = useState(null); // null | 'welcome' | 'index' | 'touring' | 'complete'
  const [visited, setVisited] = useState({});
  const [completed, setCompleted] = useState(false);

  const dikshaTitle = t('दीक्षा 👩‍🏫', 'Diksha 👩‍🏫');

  const buildSteps = useCallback((feature) => {
    const steps = [{
      popover: {
        title: `${feature.icon} ${t(feature.title_hi, feature.title_en)}`,
        description: t(feature.intro_hi, feature.intro_en),
      },
    }];
    const root = document.querySelector('main') || document.body;
    const nodes = Array.from(root.querySelectorAll(
      'button, a[data-testid], [role="button"], select[data-testid], input[data-testid]'
    ));
    const seen = new Set();
    for (const el of nodes) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue; // not visible
      const tid = el.getAttribute('data-testid') || '';
      if (tid.startsWith('nav-')) continue;
      const label = (el.getAttribute('aria-label') || el.textContent || tid || '').replace(/\s+/g, ' ').trim();
      const key = tid || label.slice(0, 30);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const copy = BUTTON_COPY[tid];
      const title = copy ? t(copy.title_hi, copy.title_en) : `👉 ${label.slice(0, 36) || t('यह option', 'This option')}`;
      const description = copy
        ? t(copy.desc_hi, copy.desc_en)
        : t(`यह "${label.slice(0, 40)}" बटन है — इसे दबाकर यह काम किया जाता है।`,
            `This is the "${label.slice(0, 40)}" control.`);
      steps.push({ element: el, popover: { title, description, side: 'bottom', align: 'start' } });
      if (steps.length >= 14) break; // keep tours digestible
    }
    return steps;
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const runDriver = useCallback((steps, feature) => {
    const drv = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: 'rgba(76, 29, 149, 0.55)',
      nextBtnText: t('आगे →', 'Next →'),
      prevBtnText: t('← पीछे', '← Back'),
      doneBtnText: t('हो गया ✓', 'Done ✓'),
      progressText: t('{{current}} / {{total}}', '{{current}} / {{total}}'),
      steps,
      onDestroyed: () => {
        setVisited((v) => ({ ...v, [feature.id]: true }));
        setPhase('index');
        // Persist this explored feature; backend marks completion + advances pipeline at 18/18.
        if (token && API) {
          fetch(`${API}/api/demo-mode/diksha-progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ feature_id: feature.id }),
          }).then((r) => r.json()).then((d) => { if (d?.completed) setCompleted(true); }).catch(() => {});
        }
      },
    });
    drv.drive();
  }, [language, token, API]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPageTour = useCallback((feature) => {
    setPhase('touring');
    navigate(`/${feature.id}`);
    // Pages load data async — poll until the page's own buttons render (or give up).
    let tries = 0;
    const poll = () => {
      tries += 1;
      const steps = buildSteps(feature);
      if (steps.length > 1 || tries >= 22) {
        runDriver(steps, feature);
      } else {
        setTimeout(poll, 220);
      }
    };
    setTimeout(poll, 600);
  }, [buildSteps, runDriver, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Launch from Header button (keeps progress — does NOT wipe visited).
  useEffect(() => {
    const h = () => setPhase('welcome');
    window.addEventListener('diksha-demo:start', h);
    return () => window.removeEventListener('diksha-demo:start', h);
  }, []);

  // Auto-open on EVERY login for school users until all 18 features are explored.
  // Defers while the first-login WelcomeTour is on screen (it dispatches the start
  // event itself on completion), so the two never overlap.
  useEffect(() => {
    if (!isSchoolUser || !token || !API) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/demo-mode/diksha-progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        if (cancelled || !d?.success) return;
        const v = {};
        (d.visited || []).forEach((id) => { v[id] = true; });
        setVisited(v);
        setCompleted(!!d.completed);
        if (d.completed) return;
        const tryOpen = (tries = 0) => {
          if (cancelled) return;
          if (document.querySelector('[data-testid="welcome-tour"]')) {
            if (tries < 40) setTimeout(() => tryOpen(tries + 1), 500);
            return; // WelcomeTour will hand off via 'diksha-demo:start'
          }
          setPhase((p) => (p ? p : 'welcome'));
        };
        setTimeout(() => tryOpen(0), 1200);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [isSchoolUser, token, API]);

  const finish = (action) => {
    setPhase(null);
    if (action === 'onboarding') navigate('/onboarding-schedule');
    if (action === 'activate' && typeof openActivateModal === 'function') openActivateModal();
  };

  if (!phase || phase === 'touring') return null;

  const Avatar = (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-2xl shadow-md shrink-0" aria-hidden>
      👩‍🏫
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="diksha-guided-demo">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            {Avatar}
            <div>
              <p className="font-bold leading-tight">{t('दीक्षा — आपकी Demo गाइड', 'Diksha — Your Demo Guide')}</p>
              <p className="text-purple-100 text-xs">{t('शिक्षा मित्र Live Demo Session', 'Shiksha Mitra Live Demo Session')}</p>
            </div>
          </div>
          <button onClick={() => setPhase(null)} className="text-white/70 hover:text-white text-2xl leading-none" aria-label="Close" data-testid="diksha-demo-close">×</button>
        </div>

        {/* WELCOME */}
        {phase === 'welcome' && (
          <div className="p-6 overflow-y-auto" data-testid="diksha-welcome">
            <div className="flex gap-3 items-start">
              {Avatar}
              <div className="bg-purple-50 border border-purple-100 rounded-2xl rounded-tl-sm p-4 text-sm text-slate-700 leading-relaxed">
                {t(
                  'नमस्ते! 🙏 मैं दीक्षा। शिक्षा मित्र के demo session में आपका स्वागत है! मैं आपको एक-एक feature का पेज खोलकर, उसके हर बटन का काम step-by-step दिखाऊँगी। नीचे से कोई भी feature चुनें — या क्रम से शुरू करें।',
                  "Namaste! 🙏 I'm Diksha. Welcome to your Shiksha Mitra demo session! I'll open each feature's page and walk you through what every button does, step by step. Pick any feature below — or start in order."
                )}
              </div>
            </div>
            <button
              onClick={() => setPhase('index')}
              className="mt-5 w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold hover:to-purple-700 transition"
              data-testid="diksha-welcome-start"
            >
              {t('चलिए शुरू करें →', "Let's start →")}
            </button>
          </div>
        )}

        {/* INDEX */}
        {phase === 'index' && (
          <>
            <div className="p-5 overflow-y-auto" data-testid="diksha-index">
              <p className="text-sm text-slate-500 mb-3">{t('कोई feature चुनें — उसका पेज खुलेगा और मैं हर बटन समझाऊँगी:', 'Pick a feature — its page opens and I explain each button:')}</p>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_FEATURES.map((f, i) => (
                  <button
                    key={f.id}
                    onClick={() => startPageTour(f)}
                    className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition text-left"
                    data-testid={`diksha-feature-${f.id}`}
                  >
                    <span className="text-xl shrink-0">{f.icon}</span>
                    <span className="text-sm font-medium text-slate-700 truncate flex-1">{t(f.title_hi, f.title_en)}</span>
                    {visited[f.id] && <span className="text-green-500 text-sm" data-testid={`diksha-visited-${f.id}`}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100">
              <button
                onClick={() => setPhase('complete')}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold hover:to-emerald-700 transition"
                data-testid="diksha-finish-demo"
              >
                {t('Demo पूरा हुआ — आगे बढ़ें →', 'Demo done — continue →')}
              </button>
            </div>
          </>
        )}

        {/* COMPLETE → Onboarding → Activation funnel */}
        {phase === 'complete' && (
          <div className="p-6 overflow-y-auto text-center" data-testid="diksha-complete">
            <div className="text-5xl mb-3">🎉</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{t('Demo पूरा हुआ!', 'Demo complete!')}</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              {t(
                'शानदार! अब अगला कदम — अपना असली स्कूल data चढ़वाने के लिए हमारे Technical Expert के साथ एक Onboarding meeting शेड्यूल करें, और फिर payment करके Activate करके पूरा access पाएं।',
                'Great! Next step — schedule an Onboarding meeting with our Technical Expert to upload your real school data, then pay to activate for full access.'
              )}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => finish('onboarding')}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-bold hover:to-indigo-700 transition"
                data-testid="diksha-go-onboarding"
              >
                📅 {t('Onboarding Schedule करें', 'Schedule Onboarding')}
              </button>
              <button
                onClick={() => finish('activate')}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold hover:to-emerald-700 transition"
                data-testid="diksha-go-activate"
              >
                🚀 {t('Activate करें — पूरा access', 'Activate — Full Access')}
              </button>
              <button
                onClick={() => setPhase('index')}
                className="w-full py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50"
                data-testid="diksha-back-to-index"
              >
                {t('↩ Features पर वापस', '↩ Back to features')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
